import React, { useEffect, useState, useCallback } from 'react';
import { emitGameEvent } from '../../core/GameEvents';

const PALETTE = {
  DARK: '#0f1b26',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
  RED: '#ef4444',
  GREEN: '#22c55e',
  YELLOW: '#eab308',
};

// Quirky death messages
const DEATH_MESSAGES = [
  "Your ship has been reduced to space confetti.",
  "That's one way to become stardust...",
  "Hull integrity: 404 Not Found",
  "Your ship decided to take an unscheduled disassembly.",
  "Achievement unlocked: Rapid Unplanned Disassembly",
  "On the bright side, you're now one with the cosmos.",
  "Your insurance company is going to love this.",
  "Plot twist: The vacuum of space won.",
  "Ship status: Existentially challenged.",
  "You've successfully donated ship parts to the void.",
  "Error: Ship.exe has stopped working.",
  "Your atoms are now free-range.",
  "Congratulations! You've discovered explosive decompression.",
  "The universe just collected its toll.",
  "Your ship has achieved maximum entropy.",
];

// Ship lost messages (more dramatic for Red zone)
const SHIP_LOST_MESSAGES = [
  "Your ship has been permanently vaporized.",
  "That ship is now one with the cosmic void. Forever.",
  "Ship status: Deleted from existence.",
  "The insurance company has some bad news...",
  "Your ship's atoms have been redistributed across the cosmos.",
  "That wasn't just damage. That was annihilation.",
  "Your ship has been promoted to debris.",
  "Fatal error: Ship no longer exists.",
];

interface DeathModalProps {
  killerName?: string;
  killerType?: 'player' | 'bot' | 'environment';
  nearestStationName: string;
  threatLevel?: 'green' | 'yellow' | 'red';
  itemsDropped?: string[];
  isShipLost?: boolean;
  lostShipClass?: string;
  onRespawn: () => void;
}

export function DeathModal({
  killerName,
  killerType,
  nearestStationName,
  threatLevel = 'green',
  itemsDropped = [],
  isShipLost = false,
  lostShipClass,
  onRespawn
}: DeathModalProps) {
  const [countdown, setCountdown] = useState(10);
  const [deathMessage] = useState(() => {
    const messages = isShipLost ? SHIP_LOST_MESSAGES : DEATH_MESSAGES;
    return messages[Math.floor(Math.random() * messages.length)];
  });

  const handleRespawn = useCallback(() => {
    emitGameEvent('requestRespawn');
    onRespawn();
  }, [onRespawn]);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleRespawn();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [handleRespawn]);

  // Get killer description
  const getKillerText = () => {
    if (!killerName) return null;
    switch (killerType) {
      case 'player':
        return <span>Destroyed by <span style={{ color: PALETTE.TEAL }}>{killerName}</span></span>;
      case 'bot':
        return <span>Destroyed by <span style={{ color: PALETTE.ORANGE }}>{killerName}</span></span>;
      case 'environment':
        return <span>Destroyed by <span style={{ color: PALETTE.DIM }}>{killerName}</span></span>;
      default:
        return null;
    }
  };

  // Get threat level color
  const getThreatColor = () => {
    switch (threatLevel) {
      case 'green': return PALETTE.GREEN;
      case 'yellow': return PALETTE.YELLOW;
      case 'red': return PALETTE.RED;
      default: return PALETTE.GREEN;
    }
  };

  // Get penalty message based on threat level
  const getPenaltyMessage = () => {
    if (isShipLost) {
      return (
        <div style={{ ...warningStyle, color: PALETTE.RED }}>
          <div style={{ fontWeight: 600, marginBottom: '8px' }}>SHIP PERMANENTLY LOST</div>
          <div style={{ fontSize: '11px', color: PALETTE.DIM }}>
            Your {lostShipClass || 'ship'} has been destroyed. You will respawn with a free starter ship.
          </div>
        </div>
      );
    }

    if (threatLevel === 'yellow' && itemsDropped.length > 0) {
      return (
        <div style={{ ...warningStyle, color: PALETTE.YELLOW }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>EQUIPMENT DROPPED</div>
          <div style={{ fontSize: '11px', color: PALETTE.DIM }}>
            {itemsDropped.length} item{itemsDropped.length > 1 ? 's' : ''} dropped and can be looted by others.
          </div>
        </div>
      );
    }

    return null;
  };

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(15, 27, 38, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 300,
    pointerEvents: 'auto',
  };

  const borderColor = isShipLost ? PALETTE.RED : getThreatColor();
  const modalStyle: React.CSSProperties = {
    backgroundColor: `${PALETTE.PANEL}cc`,
    border: `2px solid ${borderColor}`,
    borderRadius: '8px',
    padding: '32px 48px',
    textAlign: 'center',
    fontFamily: "'JetBrains Mono', monospace",
    maxWidth: '500px',
    boxShadow: `0 0 40px ${borderColor}44`,
  };

  const titleStyle: React.CSSProperties = {
    color: isShipLost ? PALETTE.RED : PALETTE.ORANGE,
    fontSize: '22px',
    fontWeight: 600,
    marginBottom: '16px',
    textTransform: 'uppercase',
    letterSpacing: '4px',
  };

  const messageStyle: React.CSSProperties = {
    color: PALETTE.LIGHT,
    fontSize: '11px',
    marginBottom: '24px',
    fontStyle: 'italic',
    lineHeight: '1.6',
  };

  const killerStyle: React.CSSProperties = {
    color: PALETTE.DIM,
    fontSize: '11px',
    marginBottom: '20px',
  };

  const warningStyle: React.CSSProperties = {
    fontSize: '11px',
    marginBottom: '16px',
    padding: '12px',
    borderRadius: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  };

  const respawnInfoStyle: React.CSSProperties = {
    color: PALETTE.DIM,
    fontSize: '11px',
    marginBottom: '24px',
  };

  const countdownStyle: React.CSSProperties = {
    color: PALETTE.ORANGE,
    fontSize: '36px',
    fontWeight: 600,
    marginBottom: '16px',
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: PALETTE.TEAL,
    color: PALETTE.DARK,
    border: 'none',
    borderRadius: '4px',
    padding: '12px 32px',
    fontSize: '11px',
    fontWeight: 600,
    fontFamily: "'JetBrains Mono', monospace",
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    transition: 'all 0.2s',
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={titleStyle}>
          {isShipLost ? 'SHIP LOST' : 'Ship Destroyed'}
        </div>

        <div style={messageStyle}>"{deathMessage}"</div>

        {killerName && (
          <div style={killerStyle}>{getKillerText()}</div>
        )}

        {getPenaltyMessage()}

        <div style={respawnInfoStyle}>
          Respawning at <span style={{ color: PALETTE.GREEN }}>{nearestStationName}</span>
          {isShipLost && <span style={{ color: PALETTE.DIM }}> with starter ship</span>}
        </div>

        <div style={countdownStyle}>{countdown}</div>

        <button
          style={buttonStyle}
          onClick={handleRespawn}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2bc5c6';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = PALETTE.TEAL;
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          Respawn Now
        </button>
      </div>
    </div>
  );
}

export default DeathModal;
